import { ChangeEvent, useState } from 'react';
import Dialog from 'components/common/Dialog/Dialog';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { cloneDeep } from 'lodash';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from 'components/common/IconButton/IconButton';
import { Form } from 'react-bootstrap';
import { DraggableRow } from 'components/common/Table/DraggableRow';

import { DashboardTabName } from 'components/dashboards/types';
import { useMutation } from '@apollo/client';
import { GET_CONFIGV2, SAVE_DASHBOARD_TABS } from 'components/dashboards/queries';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';
import { dashboardTabsState, initialDashboardTabNamesState } from 'components/dashboards/dashboards.state';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { clearGraphQLErrorMessage } from 'common/functions';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

import './AddEditTabsModal.scss';

type AddTabsModalProps = {
  dashboardName: string;
  showModal: boolean;
  handleClose: () => void;
};

function AddEditTabsModal({
  dashboardName,
  showModal,
  handleClose,
}: AddTabsModalProps): JSX.Element {
  const tabsList = useRecoilValue(dashboardTabsState);
  const initialTabNames = useRecoilValue(initialDashboardTabNamesState);
  const [tabNames, setTabNames] = useState<DashboardTabName[]>(initialTabNames);  
  const activeTabNames = tabNames.filter((tab: DashboardTabName) => !tab.tabToRemove);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;

  const [saveTabsToDashboard] = useMutation(SAVE_DASHBOARD_TABS,
    { refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName, startTime, endTime } }] });

  const onListItemDrag = (dragIndex: number, hoverIndex: number): void => {
    const reshuffleTabs = [...tabNames];
    const tab = reshuffleTabs[dragIndex];
    // remove dragged item
    reshuffleTabs.splice(dragIndex, 1);
    // insert dragged item
    reshuffleTabs.splice(hoverIndex, 0, tab);
    setTabNames(reshuffleTabs);
  };

  const onDeleteTab = (idx: number): void => {
    const updateTabs = [...tabNames];
    updateTabs[idx].tabToRemove = true;
    setTabNames(updateTabs);
  };

  const addNewTab = () => {
    const updateTabs = [...tabNames];
    updateTabs.push({id: tabNames.length, newName: '', originalName: '', tabToRemove: false});
    setTabNames(updateTabs);
  };

  const onBlurTabName = (e: ChangeEvent, idx: number) => {
    const updateTabNames = cloneDeep(tabNames);
    updateTabNames[idx].newName = (e.target as HTMLInputElement).value;
    setTabNames([...updateTabNames]);
  };

  const handleSaveTabs = () => {
    const finalTabs = tabNames.filter((fields) => fields.newName !== '').map(({id, ...fields}) => fields);
    const variables = {
      tabs: finalTabs,
      dashboardName,
    };

    saveTabsToDashboard({ variables })
      .then(() => {
        handleClose();
      })
      .catch((error) => {
        setPageAlertConfig({
          message: clearGraphQLErrorMessage(error.message),
          variant: PageAlertVariant.ERROR,
          autoHide: true,
        });
      });
  };

  return (
    <DndProvider backend={HTML5Backend}>
    <Dialog
      title={tabsList.length > 0 ? 'Edit Tabs' : 'Add Tabs'}
      show={showModal}
      close={(): void => {}}
      cancelCallback={handleClose}
      okButtonText="Save"
      okCallback={handleSaveTabs}
    >
      <table className="AddTabs">
        <tbody>
          {tabNames.map((tab: DashboardTabName, index: number) => (!tab.tabToRemove && <DraggableRow
             index={index}
             rowClass="AddTabs__Row"
             rowId={index}
             onMove={onListItemDrag}
             onDrop={() => {}}
             key={tab.id}
           >
             <td className="AddTabs__Block">
             <IconButton icon={<DragIndicatorIcon />} />
             <Form.Control
               autoComplete="off"
               type="text"
               key={tab.id}
               name="tabName"
               placeholder="Tab Name"
               onBlur={(e: ChangeEvent) => onBlurTabName(e, index)}
               defaultValue={tab.newName}
             />
             <IconButton tooltip="Delete" onClick={(): void => onDeleteTab(index)} icon={<DeleteIcon />} />
             </td>
           </DraggableRow>
          ))}
        </tbody>
      </table>
       {activeTabNames.length < 1 && <p>No tabs found</p>} 
      <Button variant={ButtonVariant.SECONDARY} onClick={addNewTab} leftIcon={<AddIcon fontSize="small"/>} className="AddTabs__AddBtn">Add</Button>
    </Dialog>
    </DndProvider>
  );
}

export default AddEditTabsModal;
